import { useRef } from "react";
import type { EditorField } from "@/lib/sign/templateEditorModel";
import { FIELD_KIND_DEFAULTS } from "@/lib/sign/templateEditorModel";

type Props = {
  field: EditorField;
  scale: number;
  selected: boolean;
  previewText?: string;
  onSelect: () => void;
  onMove: (patch: Partial<Pick<EditorField, "x" | "y">>) => void;
};

export function SignTemplateFieldOverlay({
  field,
  scale,
  selected,
  previewText,
  onSelect,
  onMove,
}: Props) {
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const kindLabel = FIELD_KIND_DEFAULTS[field.kind].label;

  function onPointerDown(e: React.PointerEvent) {
    e.stopPropagation();
    onSelect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: field.x,
      origY: field.y,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const dx = (e.clientX - dragRef.current.startX) / scale;
    const dy = (e.clientY - dragRef.current.startY) / scale;
    onMove({
      x: dragRef.current.origX + dx,
      y: dragRef.current.origY + dy,
    });
  }

  function onPointerUp(e: React.PointerEvent) {
    dragRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  }

  const border =
    field.kind === "signature"
      ? "border-indigo-500 bg-indigo-500/10"
      : field.kind === "text"
        ? "border-emerald-500 bg-emerald-500/10"
        : "border-amber-500 bg-amber-500/10";

  return (
    <div
      role="button"
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={(e) => e.stopPropagation()}
      className={`absolute cursor-move rounded border text-[10px] font-medium ${border} ${
        selected ? "ring-2 ring-blue-500 ring-offset-1" : ""
      }`}
      style={{
        left: field.x * scale,
        top: field.y * scale,
        width: field.width * scale,
        height: field.height * scale,
      }}
    >
      <span className="pointer-events-none block truncate px-1 py-0.5 text-gray-800 dark:text-zinc-100">
        {previewText ?? kindLabel}
        {field.signerRole ? ` · ${field.signerRole}` : ""}
      </span>
    </div>
  );
}
